import { createActionColumn } from './createActionColumn';
import { createBadgeColumn } from './createBadgeColumn';
import { createBooleanIndicatorColumn } from './createBooleanIndicatorColumn';
import { createCopyableColumn } from './createCopyableColumn';
import { createCustomColumn } from './createCustomColumn';
import { createDateColumn } from './createDateColumn';
import { createDisplayColumn } from './createDisplayColumn';
import { createSortableColumn } from './createSortableColumn';

import type { BooleanIndicatorColumnOptions } from './createBooleanIndicatorColumn';
import type { CustomCellContext, CustomColumnOptions } from './createCustomColumn';
import type { DisplayColumnOptions } from './createDisplayColumn';
import type {
  ActionColumnOptions,
  BadgeColumnOptions,
  ColumnFactory,
  CopyableColumnOptions,
  DateColumnOptions,
  SortableColumnOptions,
} from './types';
import type { AccessorKeyColumnDef } from '@tanstack/react-table';

/**
 * Base configuration shared by all accessor-based columns
 */
type BaseAccessorColumnConfig<TData> = {
  accessor: keyof TData & string;
  label: string;
};

/**
 * Base configuration for display columns (no accessor)
 */
type BaseDisplayColumnConfig = {
  label: string;
};

/**
 * Column configuration types for each column type
 */
type CopyableColumnConfig<TData> = BaseAccessorColumnConfig<TData> & {
  type: 'copyable';
  options?: CopyableColumnOptions<TData>;
};

type SortableColumnConfig<TData> = BaseAccessorColumnConfig<TData> & {
  type: 'sortable';
  options?: SortableColumnOptions<TData>;
};

type DateColumnConfig<TData> = BaseAccessorColumnConfig<TData> & {
  type: 'date';
  options?: DateColumnOptions<TData>;
};

type BadgeColumnConfig<TData> = BaseAccessorColumnConfig<TData> & {
  type: 'badge';
  options: BadgeColumnOptions<TData>;
};

type ActionColumnConfig<TData, TColumnId extends string = never> = BaseDisplayColumnConfig & {
  type: 'action';
  id?: (keyof TData & string) | TColumnId;
  options: ActionColumnOptions<TData>;
};

type CustomColumnConfig<TData, TColumnId extends string = never, TValue = unknown> = BaseAccessorColumnConfig<TData> & {
  type: 'custom';
  id?: (keyof TData & string) | TColumnId;
  options?: CustomColumnOptions;
  cell: (context: CustomCellContext<TData, TValue>) => React.ReactNode;
};

type CustomColumnConfigWithAccessorFn<TData, TColumnId extends string = never, TValue = unknown> = {
  type: 'custom';
  accessorFn: (row: TData) => TValue;
  id: (keyof TData & string) | TColumnId;
  label: string;
  options?: CustomColumnOptions;
  cell: (context: CustomCellContext<TData, TValue>) => React.ReactNode;
};

type DisplayColumnConfig<TData, TColumnId extends string = never> = {
  type: 'display';
  id: (keyof TData & string) | TColumnId;
  label: string;
  options?: DisplayColumnOptions;
  cell: (row: TData) => React.ReactNode;
};

type BooleanIndicatorColumnConfig<TData> = BaseAccessorColumnConfig<TData> & {
  type: 'boolean-indicator';
  options?: BooleanIndicatorColumnOptions;
};

/**
 * Union type of all column configurations.
 *
 * `TColumnId` constrains the `id` field on display/action/custom columns. Defaults to `never`,
 * meaning column IDs must be `keyof TData`. Callers whose tables have columns with IDs that
 * aren't fields of `TData` (e.g., `'actions'`, `'edit'`) declare a union for `TColumnId`.
 */
export type ColumnConfig<TData, TColumnId extends string = never> =
  | CopyableColumnConfig<TData>
  | SortableColumnConfig<TData>
  | DateColumnConfig<TData>
  | BadgeColumnConfig<TData>
  | ActionColumnConfig<TData, TColumnId>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | CustomColumnConfig<TData, TColumnId, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | CustomColumnConfigWithAccessorFn<TData, TColumnId, any>
  | DisplayColumnConfig<TData, TColumnId>
  | BooleanIndicatorColumnConfig<TData>;

export type InlineColumnFactory<TData> = Omit<AccessorKeyColumnDef<TData, unknown>, 'cell'> & {
  cell?: never;
};

/**
 * Unified factory function to create any type of table column
 */
export function createColumn<TData, TColumnId extends string = never>(
  config: ColumnConfig<TData, TColumnId>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnFactory<TData, any> {
  switch (config.type) {
    case 'copyable':
      return createCopyableColumn<TData>({
        accessor: config.accessor,
        label: config.label,
        options: config.options,
      });

    case 'sortable':
      return createSortableColumn<TData>({
        accessor: config.accessor,
        label: config.label,
        options: config.options,
      });

    case 'date':
      return createDateColumn<TData>({
        accessor: config.accessor,
        label: config.label,
        options: config.options,
      });

    case 'badge':
      return createBadgeColumn<TData>({
        accessor: config.accessor,
        label: config.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options: config.options as any,
      });

    case 'action':
      return createActionColumn<TData>({
        id: config.id,
        label: config.label,
        options: config.options,
      });

    case 'custom':
      return createCustomColumn<TData>(config as CustomColumnConfig<TData>);

    case 'display':
      return createDisplayColumn<TData>({
        id: config.id,
        label: config.label,
        options: config.options,
        cell: config.cell,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any;

    case 'boolean-indicator':
      return createBooleanIndicatorColumn<TData>({
        accessor: config.accessor,
        label: config.label,
        options: config.options,
      });

    default: {
      // Exhaustiveness check
      const _exhaustive: never = config;
      throw new Error(`Unknown column type: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/**
 * Expand an array of concise column specs into the `ColumnDef[]` a table expects.
 *
 * Binds `TData` (and optionally `TColumnId`) once for the whole list so each entry
 * no longer restates `createColumn<Row>`, while keeping the same per-column type
 * safety — `accessor`, `cell` `row`, and `id` are all checked against `TData`.
 *
 * @example
 * const COLUMNS = toTableColumns<Row>([
 *   createCreatedAtColumn<Row>(),
 *   { type: 'custom', accessor: 'requester', label: 'Requester', cell: ({ row }) => row.requester.email },
 * ]);
 */
export function toTableColumns<TData, TColumnId extends string = never>(
  configs: (ColumnConfig<TData, TColumnId> | InlineColumnFactory<TData>)[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnFactory<TData, any>[] {
  return configs.map((config) => ('type' in config ? createColumn<TData, TColumnId>(config) : config));
}
